import React, { useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { Save, X, Loader2 } from "lucide-react";

/**
 * QuickViewAssets — flat editable table for the Assets register.
 *
 * Mirrors QuickViewInventory's UX contract:
 *  - dropdowns wherever there's a canonical list (Category, Dept, Condition,
 *    Status, Assignee) → prevents typos
 *  - freeform inputs for Location, Brand, Model, Serial (these are naturally
 *    diverse and warrant free text)
 *  - row-scoped edits (yellow highlight when dirty, Save/Cancel per row) so
 *    a stray keystroke never blows away another asset's changes
 *  - single PUT hit on Save; parent re-fetches so cards & filters stay
 *    in sync
 *
 * Legacy values (e.g. a category imported before it was added to the managed
 * list) are shown as first option `(legacy)` so we never silently rewrite.
 */
const STATUSES = [
  { v: "available", l: "Available" },
  { v: "in_use", l: "In use" },
  { v: "broken_unconfirmed", l: "Broken (unconfirmed)" },
  { v: "broken", l: "Broken" },
  { v: "retired", l: "Retired" },
  { v: "lost", l: "Lost" },
];
const CONDITIONS = [
  { v: "", l: "— not set —" },
  { v: "new", l: "New" },
  { v: "good", l: "Good" },
  { v: "fair", l: "Fair" },
  { v: "worn", l: "Worn" },
  { v: "broken", l: "Broken" },
];

const QuickViewAssets = ({ items, users, categories, onSaved }) => {
  const { departments } = useDepartments();
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const usersSorted = useMemo(
    () => [...(users || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [users],
  );
  const catNames = useMemo(
    () => (categories || []).map((c) => c.name).filter(Boolean),
    [categories],
  );

  const patch = useCallback((id, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
  }, []);

  const cancel = (id) =>
    setDrafts((d) => {
      const n = { ...d };
      delete n[id];
      return n;
    });

  const save = async (item) => {
    const body = { ...(drafts[item.id] || {}) };
    if (Object.keys(body).length === 0) return;
    setSavingId(item.id);
    try {
      await api.put(`/assets/${item.id}`, body);
      toast.success(`Saved "${item.name}"`);
      cancel(item.id);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSavingId(null);
    }
  };

  const val = (item, field) =>
    drafts[item.id]?.[field] !== undefined
      ? drafts[item.id][field]
      : item[field] ?? "";

  if (!items?.length) {
    return (
      <div className="nb-card p-10 text-center text-sm text-[#607087]" data-testid="quickview-assets-empty">
        No assets match your filters.
      </div>
    );
  }

  return (
    <div className="nb-card p-0 overflow-x-auto" data-testid="quickview-assets-table">
      <table className="min-w-[1400px] w-full text-sm">
        <thead className="bg-[#F4F8FB] border-b-2 border-black">
          <tr className="text-left">
            <th className="p-3 label-eyebrow w-[16%]">Asset</th>
            <th className="p-3 label-eyebrow w-[10%]">Category</th>
            <th className="p-3 label-eyebrow w-[9%]">Dept</th>
            <th className="p-3 label-eyebrow w-[13%]">Location</th>
            <th className="p-3 label-eyebrow w-[9%]">Brand</th>
            <th className="p-3 label-eyebrow w-[9%]">Model</th>
            <th className="p-3 label-eyebrow w-[9%]">Serial</th>
            <th className="p-3 label-eyebrow w-[8%]">Condition</th>
            <th className="p-3 label-eyebrow w-[9%]">Status</th>
            <th className="p-3 label-eyebrow w-[9%]">Assignee</th>
            <th className="p-3 label-eyebrow w-[7%] text-right sticky right-0 bg-[#F4F8FB]">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => {
            const dirty = !!drafts[a.id] && Object.keys(drafts[a.id]).length > 0;
            const busy = savingId === a.id;
            return (
              <tr
                key={a.id}
                data-testid={`quickview-asset-row-${a.id}`}
                className={`border-b border-black/10 ${dirty ? "bg-[#FFF7DB]" : ""}`}
              >
                <td className="p-3 align-top">
                  <input
                    data-testid={`quickview-asset-name-${a.id}`}
                    className="qv-input font-semibold"
                    value={val(a, "name") ?? ""}
                    onChange={(e) => patch(a.id, "name", e.target.value)}
                  />
                  <div className="text-[10px] text-[#607087] mt-0.5">
                    {a.cost ? `$${Number(a.cost).toFixed(0)}` : "no cost"}
                    {a.purchased_at ? ` • ${a.purchased_at.slice(0, 7)}` : ""}
                  </div>
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-asset-category-${a.id}`}
                    className="qv-input"
                    value={val(a, "category") || ""}
                    onChange={(e) => patch(a.id, "category", e.target.value)}
                  >
                    <option value="">— pick —</option>
                    {val(a, "category") && !catNames.includes(val(a, "category")) && (
                      <option value={val(a, "category")}>
                        {val(a, "category")} (legacy)
                      </option>
                    )}
                    {catNames.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-asset-dept-${a.id}`}
                    className="qv-input"
                    value={val(a, "department") || ""}
                    onChange={(e) => patch(a.id, "department", e.target.value)}
                  >
                    <option value="">— none —</option>
                    {(departments || []).map((d) => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-asset-location-${a.id}`}
                    className="qv-input"
                    value={val(a, "location") || ""}
                    onChange={(e) => patch(a.id, "location", e.target.value)}
                    placeholder="e.g. Workshop bench 2"
                  />
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-asset-brand-${a.id}`}
                    className="qv-input"
                    value={val(a, "brand") || ""}
                    onChange={(e) => patch(a.id, "brand", e.target.value)}
                  />
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-asset-model-${a.id}`}
                    className="qv-input"
                    value={val(a, "model") || ""}
                    onChange={(e) => patch(a.id, "model", e.target.value)}
                  />
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-asset-serial-${a.id}`}
                    className="qv-input font-mono-num"
                    value={val(a, "serial") || ""}
                    onChange={(e) => patch(a.id, "serial", e.target.value)}
                  />
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-asset-condition-${a.id}`}
                    className="qv-input"
                    value={val(a, "condition") || ""}
                    onChange={(e) => patch(a.id, "condition", e.target.value)}
                  >
                    {CONDITIONS.map((c) => (
                      <option key={c.v} value={c.v}>{c.l}</option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-asset-status-${a.id}`}
                    className="qv-input"
                    value={val(a, "status") || "available"}
                    onChange={(e) => patch(a.id, "status", e.target.value)}
                  >
                    {STATUSES.map((s) => (
                      <option key={s.v} value={s.v}>{s.l}</option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  {/* Free-text assignee — the full radio picker (user/dept/text)
                   * lives in the AssetFormDialog. For quick-fill during a
                   * walk-around we default to the "text" bucket. */}
                  <input
                    data-testid={`quickview-asset-assignee-${a.id}`}
                    className="qv-input"
                    value={
                      drafts[a.id]?.assignee_text !== undefined
                        ? drafts[a.id].assignee_text
                        : a.assignee_text ||
                          (a.assignee_kind === "user"
                            ? usersSorted.find((u) => u.id === a.assignee_id)?.name || ""
                            : "")
                    }
                    onChange={(e) => {
                      patch(a.id, "assignee_kind", "text");
                      patch(a.id, "assignee_text", e.target.value);
                    }}
                    placeholder="Stephanie / Bench 2"
                  />
                </td>
                <td className="p-2 align-top text-right sticky right-0 bg-white">
                  {dirty ? (
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`quickview-asset-save-${a.id}`}
                        onClick={() => save(a)}
                        disabled={busy}
                        className="nb-btn nb-btn-primary px-2 py-1.5 text-xs flex items-center gap-1"
                      >
                        {busy ? (
                          <Loader2 size={12} className="animate-spin" strokeWidth={2.5} />
                        ) : (
                          <Save size={12} strokeWidth={2.5} />
                        )}
                        Save
                      </button>
                      <button
                        data-testid={`quickview-asset-cancel-${a.id}`}
                        onClick={() => cancel(a.id)}
                        disabled={busy}
                        className="nb-btn bg-white p-1.5"
                      >
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-[10px] text-[#607087] italic">saved</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default QuickViewAssets;
